import React, { useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import s from './Subscription.module.css';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store/store';
import {
    createCheckout,
    createPortal,
    fetchUserSubscription,
    StripeProduct,
} from '../../../../store/slices/subscriptionSlice';

interface SubscriptionConfirmationModalProps {
    product: StripeProduct | null;
    autoRenew: boolean;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    inputRedirectUrl?: string;
}

export default function SubscriptionConfirmationModal({
    product,
    autoRenew,
    isOpen,
    setIsOpen,
    inputRedirectUrl,
}: SubscriptionConfirmationModalProps) {
    const [loading, setLoading] = useState(false);
    const subscription = useSelector((state: RootState) => state.subscription.userSubscription);
    const dispatch = useAppDispatch();

    if (!isOpen) return null;

    const currentTier = subscription?.tier ?? 0;
    const productTier = Number(product?.attributes?.metadata?.tier ?? 0);

    const rawPlan = product?.attributes?.metadata?.plan || product?.attributes?.name || 'Plan';
    const productLabel = rawPlan.charAt(0).toUpperCase() + rawPlan.slice(1);

    let actionType: 'upgrade' | 'downgrade' | 'manage' = 'upgrade';
    if (currentTier === productTier) {
        actionType = 'manage';
    } else if (productTier < currentTier) {
        actionType = 'downgrade';
    }

    const proceedAction = async () => {
        if (loading || !product) return;
        setLoading(true);

        const redirectUrl = inputRedirectUrl ?? `${window.location.origin}/settings`;

        try {
            if (actionType === 'manage') {
                const portalUrl: string = await dispatch(
                    createPortal({ redirect_uri: redirectUrl }),
                ).unwrap();
                if (!portalUrl) throw new Error('Portal URL not returned');
                window.location.href = portalUrl;
                return;
            } else {
                const result = await dispatch(
                    createCheckout({
                        product: product.id,
                        redirect_uri: redirectUrl,
                        auto_renew: autoRenew,
                    }),
                ).unwrap();

                if (result.type === 'payment-redirect' && result.url) {
                    window.location.href = result.url;
                    return;
                }

                if (result.type === 'subscription-updated') {
                    toast.success('Your subscription has been updated.');
                    await dispatch(fetchUserSubscription());
                    return;
                }

                if (result.type === 'checkout-session' && result.id) {
                    const publishableKey = result.public_key;
                    if (!publishableKey) throw new Error('Missing publishable key from server');

                    const stripe: Stripe | null = await loadStripe(publishableKey);
                    if (!stripe) throw new Error('Stripe failed to load.');

                    const { error } = await stripe.redirectToCheckout({
                        sessionId: result.id,
                    });
                    if (error) {
                        toast.error(`Stripe redirect error: ${error.message}`);
                    }
                    return;
                }

                throw new Error('Unexpected checkout response');
            }
        } catch (err: any) {
            const msg = err?.message || 'Unexpected error';
            toast.error(msg);
        } finally {
            setLoading(false);
            setIsOpen(false);
        }
    };

    return (
        <div className={s.modalOverlay}>
            <div className={s.modal}>
                <h3>Confirm {actionType}</h3>
                <p>
                    {actionType === 'downgrade'
                        ? `You are about to downgrade to ${productLabel}.`
                        : actionType === 'upgrade'
                          ? `You are about to upgrade to ${productLabel}.`
                          : `You are about to open the billing portal.`}
                </p>
                <button
                    className={s.confirmBtn}
                    onClick={proceedAction}
                    disabled={loading}
                    aria-busy={loading}
                >
                    {loading
                        ? actionType === 'manage'
                            ? 'Opening portal…'
                            : actionType === 'downgrade'
                              ? 'Downgrading…'
                              : 'Upgrading…'
                        : 'Yes, Proceed'}
                </button>
                <button className={s.cancelBtn} onClick={() => setIsOpen(false)} disabled={loading}>
                    Cancel
                </button>
            </div>
        </div>
    );
}
